import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { CircleProgressOptions, NgCircleProgressModule } from 'ng-circle-progress';
import { FileSaverService } from 'ngx-filesaver';
import { CheckboxModule } from '../components/checkbox/checkbox.component';
import {
  ReferralPageBannerComponent,
  ReferralPageBannerModule,
} from '../home/referral-page-banner/referral-page-banner.component';
import { DateArrayToStringPipeModule } from '../pipes/date-array-to-string.pipe';
import { SafeUrlPipeModule } from '../pipes/safe-url.pipe';
import { ActivityModalNotificationComponent } from './activity-notification/activity-modal-notification/activity-modal-notification.component';
import { ActivityNotificationListComponent } from './activity-notification/activity-notification-list/activity-notification-list.component';
import { ActivitySubtleNotificationComponent } from './activity-notification/activity-subtle-notification/activity-subtle-notification.component';
import { ActivityPlayerComponent } from './activity-player/activity-player.component';
import { ArrowLinkModule } from './arrow-link/arrow-link.component';
import { AskTeacherComponent } from './ask-teacher/ask-teacher.component';
import { BackboneAdapterService } from './backbone-adapter.service';
import { BadgeDescriptionComponent } from './badge-description/badge-description.component';
import { BadgeModalComponent } from './badge-modal/badge-modal.component';
import { BlobProgressBarComponent } from './blob-progress-bar/blob-progress-bar.component';
import { BubblePopupComponent } from './bubble-popup/bubble-popup.component';
import { CardLockedOverlayComponent } from './card-locked-overlay/card-locked-overlay.component';
import { CarouselComponent } from './carousel/carousel.component';
import { SlideDirective } from './carousel/slide.directive';
import { CircleCloseButtonComponent } from './circle-close-button/circle-close-button.component';
import { CountdownFormatPipe } from './countdown-format.pipe';
import { DictionaryComponent } from './dictionary/dictionary.component';
import { SearchWordService } from './dictionary/search-word.service';
import { DummyLoadingContentComponent } from './dummy-loading-content/dummy-loading-content.component';
import { ECModalComponent } from './ec-modal/ec-modal.component';
import { ECVideoService } from './ec-modal/ec-video.service';
import { EduSynchModalComponent } from './edusynch-modal/edusynch-modal.component';
import { ConfigEffects } from './effects/config.effects';
import { GamificationEffects } from './effects/gamification.effects';
import { EndOfLevelCongratulationModule } from './end-of-level-congratulation/end-of-level-congratulation.module';
import { GamificationActivityPointsComponent } from './gamification-activity-points/gamification-activity-points.component';
import { GenericModalComponent } from './generic-modal/generic-modal.component';
import { GenericNotificationComponent } from './generic-notification/generic-notification.component';
import { GrammarGuideComponent } from './grammar-guide/grammar-guide.component';
import { GrammarGuideService } from './grammar-guide/grammar-guide.service';
import { GTMService } from './gtm.service';
import { ImageUtilService } from './image-util-service/image-util.service';
import { ImmersionCourseService } from './immersion-course.service';
import { IndustryContentModalComponent } from './industry-content-modal/industry-content-modal.component';
import { KeepAliveService } from './keep-alive.service';
import { LevelGoalsComponent } from './level-goals/level-goals.component';
import { LevelProgressBarComponent } from './level-progress-bar/level-progress-bar.component';
import { LiveClassPcPromoService } from './live-class-pc-promo.service';
import { LiveClassSurveyImageSelectorComponent } from './live-class-survey/live-class-survey-image-selector/live-class-survey-image-selector.component';
import { LiveClassSurveyComponent } from './live-class-survey/live-class-survey.component';
import { LocationService } from './location.service';
import { MediaPlayerComponent } from './media-player/media-player.component';
import { NgMediaPlayerService } from './media-player/ng-media-player.service';
import { MobileDictionaryComponent } from './mobile-dictionary/mobile-dictionary.component';
import { MobileGrammarGuideComponent } from './mobile-grammar-guide/mobile-grammar-guide.component';
import { MobileLockComponent } from './mobile-lock/mobile-lock.component';
import { MobileToolsComponent } from './mobile-tools/mobile-tools.component';
import { MobileTranslatorToolComponent } from './mobile-translator-tool/mobile-translator-tool.component';
import { MobileWritingComponent } from './mobile-writing/mobile-writing.component';
import { NewLevelCongratulationNotificationComponent } from './new-level-congratulation-notification/new-level-congratulation-notification.component';
import { OeAppPersonService } from './oe-app-person.service';
import { OeAppService } from './oe-app.service';
import { PaginationComponent } from './pagination/pagination.component';
import { PhoneInputComponent } from './phone-input/phone-input.component';
import { CreateArrayPipe } from './pipes/create-array.pipe';
import { NumbersSuffixesPipe } from './pipes/numbers-suffixes.pipe';
import { TranslateMediumDatePipe } from './pipes/translate-medium-date.pipe';
import { ProgressBarComponent } from './progress-bar/progress-bar.component';
import { ProgressCircleComponent } from './progress-circle/progress-circle.component';
import { sharedReducers } from './reducers/index.tx';
import { RequireAdapterService } from './require-adapter.service';
import { SpeechaceModalComponent } from './speechace-modal/speechace-modal.component';
import { SpeechaceService } from './speechace-modal/speechace.service';
import { SpinnerComponent } from './spinner/spinner.component';
import { StudyBundleChildHeaderComponent } from './study-bundle-child-header/study-bundle-child-header.component';
import { TitleBarComponent } from './title-bar/title-bar.component';
import { TitleCasePipe } from './title-case.pipe';
import { TranslatePipeModule } from './translate.pipe';
import { TranslationService } from './translation.service';
import { TranslatorToolComponent } from './translator-tool/translator-tool.component';
import { TranslatorToolService } from './translator-tool/translator-tool.service';
import { TruncateWordsPipe } from './truncate-words.pipe';
import { TypeaheadDropdownComponent } from './typeahead-dropdown/typeahead-dropdown.component';
import { WindowService } from './window.service';
import { WritingFeedbackRequestButtonComponent } from './writing-feedback-request-button/writing-feedback-request-button.component';
import { WritingFeedbackRequestModalComponent } from './writing-feedback-request-modal/writing-feedback-request-modal.component';
import { WritingComponent } from './writing/writing.component';
import { WritingService } from './writing/writing.service';

@NgModule({
  declarations: [
    BlobProgressBarComponent,
    BubblePopupComponent,
    CarouselComponent,
    DictionaryComponent,
    ECModalComponent,
    GrammarGuideComponent,
    LevelGoalsComponent,
    LevelProgressBarComponent,
    MobileDictionaryComponent,
    MobileGrammarGuideComponent,
    MobileLockComponent,
    MobileToolsComponent,
    MobileTranslatorToolComponent,
    MobileWritingComponent,
    ProgressBarComponent,
    ProgressCircleComponent,
    SlideDirective,
    StudyBundleChildHeaderComponent,
    TitleBarComponent,
    TitleCasePipe,
    TranslatorToolComponent,
    TruncateWordsPipe,
    WritingComponent,
    WritingFeedbackRequestButtonComponent,
    WritingFeedbackRequestModalComponent,
    GamificationActivityPointsComponent,
    DummyLoadingContentComponent,
    PaginationComponent,
    CountdownFormatPipe,
    CircleCloseButtonComponent,
    CardLockedOverlayComponent,
    SpeechaceModalComponent,
    IndustryContentModalComponent,
    MediaPlayerComponent,
    GenericModalComponent,
    TypeaheadDropdownComponent,
    PhoneInputComponent,
    ActivityPlayerComponent,
    CreateArrayPipe,
    BadgeModalComponent,
    EduSynchModalComponent,
    ActivitySubtleNotificationComponent,
    ActivityModalNotificationComponent,
    ActivityNotificationListComponent,
    NumbersSuffixesPipe,
    LiveClassSurveyComponent,
    AskTeacherComponent,
    BadgeDescriptionComponent,
    GenericNotificationComponent,
    NewLevelCongratulationNotificationComponent,
    TranslateMediumDatePipe,
    LiveClassSurveyImageSelectorComponent,
    SpinnerComponent,
  ],
  exports: [
    TranslatePipeModule,
    BlobProgressBarComponent,
    BubblePopupComponent,
    CarouselComponent,
    CarouselComponent,
    CardLockedOverlayComponent,
    CircleCloseButtonComponent,
    CountdownFormatPipe,
    CommonModule,
    DictionaryComponent,
    DummyLoadingContentComponent,
    ECModalComponent,
    FormsModule,
    GrammarGuideComponent,
    HttpClientModule,
    LevelGoalsComponent,
    LevelProgressBarComponent,
    MobileDictionaryComponent,
    MobileGrammarGuideComponent,
    MobileLockComponent,
    MobileToolsComponent,
    MobileTranslatorToolComponent,
    NgbModule,
    PaginationComponent,
    ProgressBarComponent,
    ReactiveFormsModule,
    SlideDirective,
    SlideDirective,
    StudyBundleChildHeaderComponent,
    TitleBarComponent,
    TitleCasePipe,
    TranslatorToolComponent,
    TruncateWordsPipe,
    WritingComponent,
    WritingFeedbackRequestButtonComponent,
    WritingFeedbackRequestModalComponent,
    GamificationActivityPointsComponent,
    MediaPlayerComponent,
    GenericModalComponent,
    TypeaheadDropdownComponent,
    PhoneInputComponent,
    CreateArrayPipe,
    BadgeModalComponent,
    ActivitySubtleNotificationComponent,
    ActivityModalNotificationComponent,
    ActivityNotificationListComponent,
    NumbersSuffixesPipe,
    LiveClassSurveyComponent,
    LiveClassSurveyImageSelectorComponent,
    AskTeacherComponent,
    GenericNotificationComponent,
    TranslateMediumDatePipe,
    ReferralPageBannerComponent,
    SpinnerComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    NgCircleProgressModule,
    NgbModule,
    SafeUrlPipeModule,
    ReferralPageBannerModule,
    TranslatePipeModule,
    ArrowLinkModule,
    StoreModule.forFeature('shared', sharedReducers),
    EffectsModule.forFeature([GamificationEffects, ConfigEffects]),
    EndOfLevelCongratulationModule,
    CheckboxModule,
    DateArrayToStringPipeModule,
  ],
  providers: [
    { provide: 'MediaPlayerService', useClass: NgMediaPlayerService },
    BackboneAdapterService,
    CircleProgressOptions,
    ECVideoService,
    GrammarGuideService,
    GTMService,
    ImageUtilService,
    FileSaverService,
    KeepAliveService,
    LiveClassPcPromoService,
    LocationService,
    OeAppService,
    OeAppPersonService,
    RequireAdapterService,
    SearchWordService,
    TranslationService,
    TranslatorToolService,
    SpeechaceService,
    WindowService,
    WritingService,
    ImmersionCourseService,
  ],
  entryComponents: [
    ECModalComponent,
    EduSynchModalComponent,
    MediaPlayerComponent,
    GenericModalComponent,
    WritingFeedbackRequestModalComponent,
    SpeechaceModalComponent,
    IndustryContentModalComponent,
    BadgeModalComponent,
    ActivitySubtleNotificationComponent,
    ActivityModalNotificationComponent,
    LiveClassSurveyComponent,
    GenericNotificationComponent,
    NewLevelCongratulationNotificationComponent,
  ],
})
export class SharedModule {}



// WEBPACK FOOTER //
// ./ts/app/shared/shared.module.ts